import { useApolloClient, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import {
  EntityPagesArticleModuleFeatureArticleDocument,
  EntityPagesArticleModuleFeatureArticleMutationVariables,
} from '../graphql/mutations/entity-pages-article-module-feature-article.gql-types';
import { EntityPagesArticleModuleListDocument } from '../graphql/queries/entity-pages-article-module-list.gql-types';

import { useArticlesModuleContext } from './use-articles-module-context/use-articles-module-context';

export const useFeatureArticleMutation = (): [
  (
    variables: EntityPagesArticleModuleFeatureArticleMutationVariables
  ) => Promise<void>,
  { loading: boolean },
] => {
  const { variablesQuery, refetch } = useArticlesModuleContext();
  const apolloClient = useApolloClient();
  const { $t } = useIntl();

  const [handle, { loading }] = useMutation(
    EntityPagesArticleModuleFeatureArticleDocument
  );

  return [
    useCallback(
      async (
        variables: EntityPagesArticleModuleFeatureArticleMutationVariables
      ) => {
        try {
          const { data, errors } = await handle({
            variables,
          });

          const error = data?.contentFeatureArticle?.error || errors?.[0];
          if (error) {
            throw Error($t({ id: 'EP_GENERAL_FEEDBACK_ERROR' }), {
              cause: error,
            });
          }
        } catch (error) {
          throw Error($t({ id: 'EP_GENERAL_FEEDBACK_ERROR' }), {
            cause: error,
          });
        }

        const existingData = apolloClient.readQuery({
          query: EntityPagesArticleModuleListDocument,
          variables: variablesQuery,
        });
        const { contentPageArticles } = existingData ?? {};

        if (contentPageArticles?.edges === undefined) {
          refetch();
          return;
        }

        const newContentPageFeaturedArticle = contentPageArticles.edges.find(
          (edge) => edge?.node.object.globalId === variables.input.globalId
        )?.node;

        if (!newContentPageFeaturedArticle) {
          refetch();
          return;
        }

        const newContentPageArticles = {
          ...contentPageArticles,
          edges: contentPageArticles.edges.filter(
            (edge) => edge?.node.object.globalId !== variables.input.globalId
          ),
        };

        apolloClient.cache.writeQuery({
          query: EntityPagesArticleModuleListDocument,
          variables: variablesQuery,
          data: {
            ...existingData,
            contentPageArticles: newContentPageArticles,
            contentPageFeaturedArticle: {
              ...newContentPageFeaturedArticle,
              isFeatured: true,
            },
          },
        });

        return;
      },
      [handle, $t, apolloClient, variablesQuery, refetch]
    ),
    { loading },
  ];
};
